<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">directions_car</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Edit Vehicle</h2>
        <p class="text-sm text-gray-500">Update this vehicle's details.</p>
      </div>
    </div>

    <form @submit.prevent="edit" class="space-y-6">
      <!-- Vehicle details -->
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div class="flex items-center justify-between mb-5">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">directions_car</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Vehicle Details</span>
          </div>

          <!-- Profile complete toggle -->
          <label class="flex items-center gap-3 cursor-pointer">
            <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">Profile Complete</span>
            <div class="relative inline-block w-16 align-middle transition duration-200 ease-in select-none">
              <input
                type="checkbox"
                name="toggle"
                v-model="vehicle.completed"
                id="toggle"
                class="absolute block w-8 h-8 bg-white border-4 rounded-full appearance-none cursor-pointer toggle-checkbox"
              />
              <label
                for="toggle"
                class="block h-8 overflow-hidden bg-gray-300 rounded-full cursor-pointer toggle-label"
              ></label>
            </div>
          </label>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label class="block sm:col-span-2">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle Type</span>
            <select
              @change="setVehicleType"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
            >
              <option value="" disabled selected hidden>Select a vehicle type</option>
              <option
                v-for="vehicleType in vehicleTypes"
                :value="vehicleType._id"
                :key="`vt-opt-${vehicleType._id}`"
                :selected="vehicle.vehicleType === vehicleType._id"
              >
                {{ vehicleType.name }}
              </option>
            </select>
          </label>

          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Model</span>
            <input
              v-model="vehicle.modelName"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Yaris"
            />
          </label>

          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Year</span>
            <input
              v-model="vehicle.modelYear"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="2009"
            />
          </label>

          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Plate Number</span>
            <input
              @blur="validatePlateNumber"
              v-model="vehicle.plateNumber"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              :class="{ 'border-red-400 focus:border-red-400': plateNumberError }"
              placeholder="3-AA-XXXXXX"
            />
            <p v-if="plateNumberError" v-text="plateNumberError" class="mt-1 text-xs text-red-500"></p>
          </label>

          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Color</span>
            <input
              v-model="vehicle.color"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Teal"
            />
          </label>

          <label class="block sm:col-span-2">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">VIN</span>
            <input
              @blur="validateVIN"
              @keyup="vehicle.vin = vehicle.vin.toUpperCase()"
              v-model="vehicle.vin"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              :class="{ 'border-red-400 focus:border-red-400': vinValidationError }"
              placeholder="(Optional)"
            />
            <p v-if="vinValidationError" v-text="vinValidationError" class="mt-1 text-xs text-red-500"></p>
          </label>
        </div>
      </div>

      <!-- Documents -->
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div class="flex items-center mb-5 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">description</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Documents</span>
        </div>

        <div class="flex flex-wrap gap-8">
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle Photo</span>
            <photo-uploader v-model="vehicle.photo" label="" />
          </div>
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Registration</span>
            <photo-uploader v-model="vehicle.libre" label="" />
          </div>
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">3rd Party Insurance</span>
            <photo-uploader v-model="vehicle.insurance" label="" />
          </div>
        </div>
      </div>

      <!-- Driver assignment -->
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div class="flex items-center mb-5 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">person</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Driver</span>
        </div>

        <div class="relative">
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Assign Driver</span>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedDriver"
                @keyup="searchDriver"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
          </label>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <div v-if="driverSearchResults.length" class="absolute inset-x-0 z-10 mt-1">
              <div class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64">
                <ul>
                  <li
                    v-for="(result, index) in driverSearchResults"
                    :key="result._id"
                    @click="!result.disabled ? selectDriver(index) : () => {}"
                    :disabled="result.disabled"
                    class="flex items-center px-4 py-2 space-x-3 transition-colors duration-150 cursor-pointer hover:bg-taxiye-50"
                    :class="{
                      'bg-gray-50 text-gray-400 cursor-not-allowed hover:bg-gray-50':
                        result.disabled,
                    }"
                  >
                    <img
                      class="object-cover w-10 h-10 rounded-full"
                      :src="result.profileImage"
                      :alt="`${result.firstName} ${result.lastName}`"
                    />
                    <div>
                      <p class="text-sm font-medium text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">{{ result.phoneNumber }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end gap-3">
        <back-button></back-button>
        <button
          :disabled="busy || !valid"
          class="inline-flex items-center h-10 px-5 text-sm font-semibold text-white shadow-sm rounded-xl transition-colors duration-150"
          :class="busy || !valid ? 'bg-taxiye-200 cursor-not-allowed' : 'bg-taxiye-500 hover:bg-taxiye-600'"
        >
          <span class="mr-1 text-base material-icons-round">check</span>
          Save Changes
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import PhotoUploader from "@/components/PhotoUploader.vue";
import BackButton from "@/components/BackButton.vue";

export default {
  components: {
    PhotoUploader,
    BackButton,
  },
  data() {
    return {
      busy: false,
      vehicleTypes: [],
      vehicle: {
        modelYear: "",
        modelName: "",
        color: "",
        libre: "",
        insurance: "",
        plateNumber: "",
        vehicleType: "",
        driver: "",
        photo: "",
        vin: "",
        completed: false,
      },
      plateNumberError: "",
      vehiclePlateNumber: "",
      vinValidationError: null,
      selectedDriver: "",
      drivers: [],
      driverSearchResults: [],
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    valid() {
      return !(this.vinValidationError || this.plateNumberError)
    }
  },
  created() {
    this.$misc
      .get(`/vehicles/${this.id}`)
      .then((response) => {
        this.vehicle = response.data;
        this.vehiclePlateNumber = response.data.plateNumber;
      })
      .catch((err) => console.log(err));

    this.$misc
      .get("vehicleTypes")
      .then((response) => (this.vehicleTypes = response.data.data))
      .catch((err) => console.log(err));

    this.$misc
      .get("drivers")
      .then((response) => (this.drivers = response.data.data))
      .catch((err) => console.log(err));
  },
  methods: {
    setVehicleType(e) {
      this.vehicle.vehicleType = e.target.value;
    },
    edit() {
      this.busy = true;
      this.$misc
        .patch(`vehicles/${this.id}`, this.vehicle)
        .then(() => {
          window.flash({
            body: "Vehicle edited successfully!",
            type: "success",
          });
          this.$router.push("/vehicles");
        })
        .catch((err) => {
          window.flash({ body: "Failed to edit vehicle!", type: "error" });
          console.log(err);
        })
        .finally(() => (this.busy = false));
    },
    validateVIN() {
      this.vehicle.vin = this.vehicle.vin.toUpperCase();
      if (!this.vehicle.vin.length) {
        this.vinValidationError = "";
      } else {
        if (this.vehicle.vin.length < 11)
          this.vinValidationError = "Invalid VIN format! (min: 11)";
        else if (this.vehicle.vin.length > 17)
          this.vinValidationError = "Invalid VIN format! (max: 17)";
        else if (/[^A-Z\d]$/.test(this.vehicle.vin)) {
          this.vinValidationError =
            "Invalid VIN format! (only ALL CAPs and Numbers are allowed) ";
        } else {
          this.vinValidationError = "";
        }
      }
    },
    async validatePlateNumber() {
      // let exp = /^[13]{1}-[A-Z]{2}-[A-Z]?\d{5}$/;
      // if (!exp.test(this.vehicle.plateNumber)) {
      //   this.plateNumberError = "Invalid plate number!";
      //   return;
      // }

      this.plateNumberError = "validating... please wait";

      if (this.vehiclePlateNumber !== this.vehicle.plateNumber) {
        const { data: isTaken } = await this.$misc.get(`/vehicles/is-taken`, {
          params: {
            plateNumber: this.vehicle.plateNumber,
          },
        });
        if (isTaken) {
          this.plateNumberError = "plate number is taken.";
        } else {
          this.plateNumberError = "";
        }
      } else {
        this.plateNumberError = "";
      }
    },
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
      } else {
        this.$misc
          .get(`/drivers/search?q=${e.target.value.trim()}`)
          .then((response) => {
            this.driverSearchResults = response.data.map((driver) => {
              if (driver.vehicle) {
                driver.disabled = true;
              } else {
                driver.disabled = false;
              }
              return driver;
            });
          })
          .catch((err) => console.log(err));
      }
      // if (e.target.value.trim() === "") {
      //   this.driverSearchResults = [];
      // } else if (!isNaN(parseInt(e.target.value))) {
      //   this.driverSearchResults = this.drivers.filter(driver => {
      //     return driver.phoneNumber.slice(-4).includes(e.target.value);
      //   });
      // } else {
      //   this.driverSearchResults = this.drivers.filter(driver => {
      //     let name = driver.firstName + " " + driver.lastName;
      //     return name.toLowerCase().includes(e.target.value.toLowerCase());
      //   });
      // }
    },
    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.vehicle.driver = driver._id;
      this.driverSearchResults = [];
    },
  },
};
</script>
<style>
.toggle-checkbox:checked {
  @apply right-0;
  @apply border-green-400;
  right: 0;
  border-color: #68d391;
}
.toggle-checkbox:checked + .toggle-label {
  @apply bg-green-400;
  background-color: #68d391;
}
</style>